import { useCallback, useState } from "react";
import type { CreatedTicket } from "./RequestModalShell";
import { getCreatedTicket } from "./createdTicket";
import { describeParticipantsError } from "../../utils/participants";

const DEFAULT_FAILURE =
  "We could not create the ticket. Please try again in a moment.";

const ADD_FROM_DETAILS = "You can add them from the ticket details.";

/**
 * Sending a request and the three states around it: in flight, refused, and
 * created. The modal keeps its fields if it fails, so the user can retry
 * without typing everything again.
 */
export function useTicketSubmission(onTicketCreated?: () => void) {
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedTicket | null>(null);

  /**
   * `afterCreate` runs once the ticket exists, with its key: adding the
   * participants picked in the form (CTX-1256). The ticket is already in
   * Jira by then, so a failure there is a warning on the receipt, never an
   * error that would invite the user to create the ticket a second time.
   */
  const submit = useCallback(
    async (
      create: () => Promise<{ data: unknown }>,
      afterCreate?: (ticketKey: string) => Promise<unknown>
    ) => {
      setIsSaving(true);
      setSubmitError(null);
      let ticket: CreatedTicket;
      try {
        ticket = getCreatedTicket((await create()).data);
      } catch (err) {
        console.error("Error creating ticket =>", err);
        setSubmitError(DEFAULT_FAILURE);
        setIsSaving(false);
        return;
      }

      if (afterCreate) {
        ticket = await runAfterCreate(ticket, afterCreate);
      }
      setCreated(ticket);
      onTicketCreated?.();
      setIsSaving(false);
    },
    [onTicketCreated]
  );

  const resetSubmission = useCallback(() => {
    setIsSaving(false);
    setSubmitError(null);
    setCreated(null);
  }, []);

  return {
    isSaving,
    submitError,
    created,
    submit,
    /** For a check that stops the request before it is sent. */
    refuse: setSubmitError,
    resetSubmission,
  };
}

async function runAfterCreate(
  ticket: CreatedTicket,
  afterCreate: (ticketKey: string) => Promise<unknown>
): Promise<CreatedTicket> {
  if (!ticket.key) {
    return {
      ...ticket,
      warning: `The participants were not added because we could not read the new ticket's number. ${ADD_FROM_DETAILS}`,
    };
  }
  try {
    await afterCreate(ticket.key);
    return ticket;
  } catch (err) {
    console.error("Error adding participants to the new ticket =>", err);
    return {
      ...ticket,
      warning: `${describeParticipantsError(err)} ${ADD_FROM_DETAILS}`,
    };
  }
}
